import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useApi, useToast, useRouter } from '@/composables/plugins';
import type { PartnerProjectSummary } from '@cutr/constants/order';
import type { OrderSummary } from '@/types/order';
import { useRoute } from '@/composables/plugins';
import type { ToBeFixed } from '@cutr/constants/utility-types';
import api, { toastMessageFromAxiosError } from '@/helpers/api';
import { useTrack } from '@/composables/plugins';
import dayjs from 'dayjs';
import { assertValidOrderTab } from './navigation';
import { useTranslation } from 'i18next-vue';
import { AxiosError } from 'axios';
import { useOrderStore } from '@/stores/order';
import { useOrderFiles } from '@/stores/orderFiles';
import type { RouteLocationAsRelative } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useFilesManagement } from '@/composables/useFilesManagement';
import { useOrderNavigation } from '@/components/pages/Order/composables/useOrderNavigation';

export const useActiveVariantFiles = () =>
  computed(() => useOrderFiles().files);
export const useActiveVariantCADFiles = () =>
  computed(() => useOrderFiles().filesForQuoting);

export function useSelectActiveVariant() {
  const router = useRouter();

  function selectVariant(variantId: string) {
    const route = router.currentRoute;
    const urlVariant = route.value.query.variantId;
    if (urlVariant === variantId) return;

    const action = urlVariant ? 'push' : 'replace';
    const query = { ...route.value.query, variantId };
    return router[action]({ query });
  }

  return {
    selectVariant,
  };
}

export const useOrderActions = () => {
  const store = useOrderStore();

  return {
    reload: store.reloadDataForCurrentOrder,
    update: store.applyDataToCurrentOrderAndSave,
    save: store.saveOrder,
  };
};

export function useActiveFileSelection() {
  const route = useRoute();
  const router = useRouter();
  const orderFilesStore = useOrderFiles();
  const navigation = useOrderNavigation();

  const urlActiveFileId = computed(() => (route.query.fileId as string) || '');

  function loadActiveFile(fileId: string) {
    if (!fileId || !orderFilesStore.getFileById(fileId)) {
      return false;
    }

    orderFilesStore.clearViewingRevision();
    orderFilesStore.activeFileId = fileId;

    if (['files', 'positions'].includes(navigation.currentStep.value)) {
      orderFilesStore.viewed(fileId);
    }

    navigation.updateValidation();
    return true;
  }

  async function selectActiveFile(fileId: string) {
    if (!loadActiveFile(fileId)) {
      return;
    }

    await assertFileIdInUrl(fileId);
  }

  async function assertFileIdInUrl(fileId: string) {
    if (urlActiveFileId.value === fileId) return;

    const urlFile = route.query.fileId;
    const query = { ...route.query, fileId };
    const action = urlFile ? 'push' : 'replace';
    await router[action]({ query });
  }

  return { selectActiveFile, loadActiveFile, assertFileIdInUrl };
}

export type PartsSummary = {
  edgebandingSummary: Record<string, ToBeFixed>;
  nestingSummary: Record<string, ToBeFixed>[];
  nodesSummary: Record<string, ToBeFixed>[];
};

const summaryData = ref({} as PartsSummary);
const loadingMaterialData = ref(false);
export function usePartsSummary() {
  const { currentVariant, currentOrder, orderId } =
    storeToRefs(useOrderStore());

  function loadPartsSummary() {
    if (loadingMaterialData.value) return;
    if (!currentVariant.value?.id) return;
    if (!orderId.value) return;

    loadingMaterialData.value = true;

    return api
      .get(`/v2/orders/${orderId.value}/parts/summary`, {
        params: { orderVariantId: currentVariant.value.id },
      })
      .then(({ data }) => {
        summaryData.value = data.data;
      })
      .finally(() => {
        loadingMaterialData.value = false;
      });
  }

  watch(() => currentVariant.value?.id, loadPartsSummary);
  watch(() => currentOrder.value, loadPartsSummary, { immediate: true });

  return summaryData;
}

export const usePriceEstimate = () => {
  const { currentVariant, orderId } = storeToRefs(useOrderStore());

  const route = useRoute();
  const track = useTrack();

  const priceEstimate = ref();
  const stopPolling = ref(false);
  const isPolling = ref(false);
  const isStale = ref(false);
  const estimatedProgress = ref(0);

  const populatePriceEstimate = async () => {
    if (!currentVariant.value?.id || currentVariant.value?.cutlistId) return;
    const resp = await api
      .get(`/v2/order-variants/${currentVariant.value?.id}/price-estimate`)
      .catch((e) => {
        if (e.response.status === 404) return null;
        throw e;
      });
    if (!resp) return;
    priceEstimate.value = resp.data?.data;
    isStale.value = resp.data?.isStale;
    if (!['Running', 'Pending'].includes(priceEstimate.value?.status)) return;
    const startedAt = dayjs(
      resp.data?.data.startedAt || resp.data?.data.createdAt
    );
    if (startedAt && estimatedProgress.value === 0) {
      estimatedProgress.value = Math.min(
        80,
        Math.round((dayjs().diff(startedAt, 'millisecond') / 60000) * 100)
      );
      incrementProgress();
    }
  };

  const startPolling = async () => {
    if (stopPolling.value) {
      isPolling.value = false;
      return;
    }
    isPolling.value = true;
    try {
      await populatePriceEstimate();
    } catch (e) {
      console.error(e);
    }
    if (!priceEstimate.value || isStale.value) {
      isPolling.value = false;
      return;
    }
    if (estimatedProgress.value === 0) {
      incrementProgress();
    }
    if (!['Running', 'Pending'].includes(priceEstimate.value.status)) {
      estimatedProgress.value = 100;
      setTimeout(() => {
        estimatedProgress.value = 0;
      }, 3000);
      isPolling.value = false;
      track('order:estimate-calculated', {
        orderVariantId: currentVariant.value?.id,
        orderId: orderId.value,
        status: priceEstimate.value.status,
        errorMessage: priceEstimate.value.errorMessage,
        minTotalAmount: priceEstimate.value.minTotalAmount,
        maxTotalAmount: priceEstimate.value.maxTotalAmount,
        page: route.name,
      });
      return;
    }
    setTimeout(startPolling, 5000);
  };

  const triggerPriceEstimate = () => {
    track('order:estimate-requested', {
      orderVariantId: currentVariant.value?.id,
      orderId: orderId.value,
    });
    api
      .post(`/v2/order-variants/${currentVariant.value?.id}/price-estimate`)
      .then(() => {
        startPolling();
      })
      .catch(console.error);
  };

  function incrementProgress() {
    if (stopPolling.value || isStale.value) {
      estimatedProgress.value = 0;
      return;
    }
    if (estimatedProgress.value === 0) {
      estimatedProgress.value = 1;
    }
    const progress = estimatedProgress.value;
    const timeout =
      progress > 90 ? 3000 : progress * Math.round(Math.random() * 20);
    setTimeout(() => {
      if (
        estimatedProgress.value < 99 &&
        ['Running', 'Pending'].includes(priceEstimate.value.status)
      ) {
        estimatedProgress.value += 1;
        incrementProgress();
      }
    }, timeout);
  }

  watch(
    currentVariant,
    async () => {
      await populatePriceEstimate();
      if (
        ['Pending', 'Running'].includes(priceEstimate.value?.status) &&
        !isPolling.value
      ) {
        startPolling();
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    stopPolling.value = true;
  });

  return {
    priceEstimate,
    isStale,
    populatePriceEstimate,
    triggerPriceEstimate,
    estimatedProgress,
  };
};

export function useRouteIds() {
  const route = useRoute();
  return {
    orderId: computed(() => route.params.id as string),
    fileId: computed(() => route.query.fileId as string),
    variantId: computed(() => route.query.variantId as string),
    positionId: computed(
      // positionId is stored in params inside positions tab, but in query outside of it. This allows us to use the same composable in both contexts.
      () => (route.params.positionId ?? route.query.positionId) as string
    ),
  };
}

// Where a file lives now that positions has taken over the files tab. Every
// `order:files` navigation means "open this file where it lives", so a file that
// belongs to a position resolves to that position, and an order-level (still
// unassigned) file resolves to the inbox that lists it.
export function useResolveFilesDestination() {
  const route = useRoute();
  const files = useOrderFiles();

  return function resolveFilesDestination(options?: RouteLocationAsRelative) {
    const { params, query, ...rest } = options || {};
    const mergedQuery = { ...route.query, ...query };
    const positionId = files.getFileById(
      mergedQuery.fileId as string
    )?.positionId;

    // positionId and tab exist only on the position route. Copying the whole
    // params object onto the positions list makes vue-router discard them.
    if (positionId) {
      return {
        ...rest,
        name: 'order:positions:position',
        params: {
          id: route.params.id,
          ...params,
          positionId,
          tab: route.params.tab,
        },
        query: mergedQuery,
      };
    }

    return {
      ...rest,
      name: 'order:positions',
      params: { id: route.params.id, ...params },
      query: mergedQuery,
    };
  };
}

// Builds a location that keeps the current query and the order id.
export function useBuildOrderTabLocation() {
  const route = useRoute();
  const { isPositionsOrder } = useFilesManagement();
  const resolveFilesDestination = useResolveFilesDestination();

  return function buildOrderTabLocation(
    tabName: string,
    options?: RouteLocationAsRelative
  ) {
    assertValidOrderTab(tabName);

    if (tabName === 'files' && isPositionsOrder.value) {
      return resolveFilesDestination(options);
    }

    const { params, query, ...rest } = options || {};
    return {
      ...rest,
      name: `order:${tabName}`,
      params: { id: route.params.id, ...params },
      query: { ...route.query, ...query },
    };
  };
}

export function useProjectSummary() {
  const api = useApi();
  const { currentVariant } = storeToRefs(useOrderStore());

  const isLoading = ref(false);
  const projectSummary = ref<PartnerProjectSummary | null>(null);

  async function fetchProjectSummary() {
    if (!currentVariant.value) return;
    try {
      isLoading.value = true;
      const response = await api.get<{ data: PartnerProjectSummary }>(
        `/v2/order-variants/${currentVariant.value.id}/project-summary`
      );
      projectSummary.value = response.data.data;
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  }

  watch(
    currentVariant,
    () => {
      fetchProjectSummary();
    },
    { immediate: true }
  );

  return {
    isLoading,
    projectSummary,
    fetchProjectSummary,
  };
}

export function useOrderSubscription() {
  const api = useApi();
  const toast = useToast();
  const { t } = useTranslation();

  const isLoading = ref(false);

  async function updateSubscription(orderId: string, subscribed: boolean) {
    if (isLoading.value) return;
    isLoading.value = true;

    try {
      await api.post(`/v2/orders/${orderId}/subscription`, {
        subscribed,
      });
    } catch (error) {
      console.error('Failed to toggle subscription:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  function getToggleFollowButton(order: OrderSummary) {
    return {
      icon: order.isSubscribed ? 'notifications_off' : 'notifications',
      class: 'white',
      label: order.isSubscribed
        ? t('workspace:menuButtons.unfollow')
        : t('workspace:menuButtons.follow'),
      onClick: async () => {
        try {
          await updateSubscription(order.id, !order.isSubscribed);
          toast(t('common:messages.saved'));
          order.isSubscribed = !order.isSubscribed;
        } catch (error: unknown) {
          if (error instanceof AxiosError && error.response?.status === 409) {
            toast.warning(
              toastMessageFromAxiosError(
                error,
                t('common:errors.genericRequestFail')
              ),
              { duration: 5000 }
            );
            return;
          }
          toast.error(t('common:errors.genericRequestFail'));
        }
      },
    };
  }

  return {
    isLoading,
    updateSubscription,
    getToggleFollowButton,
  };
}
